/**
 * HabitMonthCalendar — an interactive month-grid calendar for a single habit.
 *
 * Renders a real month grid (weeks as rows, Mon–Sun columns) where clicking any
 * non-future day toggles that habit's completion for that date — including
 * backfilling past days. Leading blank cells align day 1 to the correct weekday
 * (week starts Monday). Completed days are filled with the habit color and a 🔥;
 * uncompleted past/today days are clickable; future days are disabled.
 *
 * Data for the visible month is loaded via useHabitStore.getHeatmap, mirroring
 * HabitEmojiCalendar's loading pattern (useEffect + cancelled flag + spinner).
 * Clicks optimistically flip the local completed-set and trigger a reload to
 * stay consistent. Does NOT change the backend or data model.
 *
 * @see Requirements 8.6
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import type { Habit } from '../../types';
import { useHabitStore } from '../../stores';

export interface HabitMonthCalendarProps {
  /** The habit to display the month calendar for. */
  habit: Habit;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Format a Date to YYYY-MM-DD string (local time). */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Build a YYYY-MM-DD string for a specific year/month(0-based)/day without
 * going through Date to avoid any timezone surprises.
 */
function makeDateStr(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

/**
 * Day-of-week with Mon=0, Tue=1, ..., Sun=6 for a given year/month/day.
 * Mirrors the Mon=0 logic from Heatmap.tsx getDayOfWeek.
 */
function getMondayDayOfWeek(year: number, month: number, day: number): number {
  const jsDay = new Date(year, month, day).getDay(); // Sun=0..Sat=6
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** Local date-only timestamp for today (midnight local). */
function todayLocalMidnight(): number {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

/** Parse a YYYY-MM-DD string into a local date-only timestamp (midnight local). */
function parseLocalMidnight(dateStr: string): number {
  const parts = dateStr.split('-').map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day).getTime();
}

export function HabitMonthCalendar({ habit }: HabitMonthCalendarProps) {
  const getHeatmap = useHabitStore((s) => s.getHeatmap);
  const toggleCompletionForDate = useHabitStore((s) => s.toggleCompletionForDate);

  // Viewed month state — default to the current month.
  const now = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState<number>(now.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(now.getMonth()); // 0-based

  const [completedDates, setCompletedDates] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const todayMidnight = useMemo(() => todayLocalMidnight(), []);
  const todayStr = useMemo(() => formatDate(new Date()), []);

  // Is the viewed month the current (real) month? Future navigation is blocked.
  const isCurrentMonth =
    viewYear === now.getFullYear() && viewMonth === now.getMonth();

  const daysInMonth = useMemo(
    () => new Date(viewYear, viewMonth + 1, 0).getDate(),
    [viewYear, viewMonth]
  );

  const firstDayStr = useMemo(
    () => makeDateStr(viewYear, viewMonth, 1),
    [viewYear, viewMonth]
  );
  const lastDayStr = useMemo(
    () => makeDateStr(viewYear, viewMonth, daysInMonth),
    [viewYear, viewMonth, daysInMonth]
  );

  // Load completions for the visible month.
  useEffect(() => {
    let cancelled = false;

    async function loadMonth() {
      setIsLoading(true);
      try {
        const data = await getHeatmap(habit.habitId, firstDayStr, lastDayStr);
        if (!cancelled) {
          setCompletedDates(
            new Set(data.filter((e) => e.completed).map((e) => e.date))
          );
        }
      } catch {
        // Error is handled by the store
        if (!cancelled) {
          setCompletedDates(new Set());
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadMonth();

    return () => {
      cancelled = true;
    };
  }, [habit.habitId, firstDayStr, lastDayStr, getHeatmap, reloadToken]);

  const leadingBlanks = useMemo(
    () => getMondayDayOfWeek(viewYear, viewMonth, 1),
    [viewYear, viewMonth]
  );

  const goPrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goNextMonth = useCallback(() => {
    if (isCurrentMonth) return; // never navigate past the current month
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, [isCurrentMonth]);

  const handleDayClick = useCallback(
    async (e: React.MouseEvent, dateStr: string) => {
      // Prevent opening the habit detail panel / row selection.
      e.stopPropagation();

      // Optimistically flip the local completed-set so the cell updates instantly.
      setCompletedDates((prev) => {
        const next = new Set(prev);
        if (next.has(dateStr)) {
          next.delete(dateStr);
        } else {
          next.add(dateStr);
        }
        return next;
      });

      try {
        await toggleCompletionForDate(habit.habitId, dateStr);
      } finally {
        // Reload to reconcile with persisted state regardless of outcome.
        setReloadToken((t) => t + 1);
      }
    },
    [habit.habitId, toggleCompletionForDate]
  );

  const monthLabel = `${MONTH_NAMES[viewMonth]} ${viewYear}`;
  const habitColor = habit.color || 'var(--color-primary)';

  return (
    <div className="flex flex-col gap-2">
      {/* Header: month label + prev/next navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goPrevMonth}
          aria-label="Previous month"
          className="flex items-center justify-center w-8 h-8 rounded text-text-secondary hover:bg-bg-secondary transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <span className="text-sm font-medium text-text-primary" aria-live="polite">
          {monthLabel}
        </span>

        <button
          type="button"
          onClick={goNextMonth}
          disabled={isCurrentMonth}
          aria-label="Next month"
          className="flex items-center justify-center w-8 h-8 rounded text-text-secondary hover:bg-bg-secondary transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1" aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="flex items-center justify-center text-xs text-text-secondary font-medium h-6"
          >
            {label}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div
          className="flex items-center justify-center py-6"
          aria-busy="true"
          aria-label="Loading calendar"
        >
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div
          role="grid"
          aria-label={`${habit.name} completion calendar for ${monthLabel}`}
          className="grid grid-cols-7 gap-1"
        >
          {/* Leading blank cells so day 1 lands on the correct weekday */}
          {Array.from({ length: leadingBlanks }).map((_, i) => (
            <div
              key={`blank-${i}`}
              aria-hidden="true"
              className="min-h-9 h-9"
            />
          ))}

          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, idx) => {
            const day = idx + 1;
            const dateStr = makeDateStr(viewYear, viewMonth, day);
            const completed = completedDates.has(dateStr);
            const isFuture = parseLocalMidnight(dateStr) > todayMidnight;
            const isToday = dateStr === todayStr;

            let ariaLabel: string;
            if (isFuture) {
              ariaLabel = `No logging for future date ${dateStr}`;
            } else if (completed) {
              ariaLabel = `${habit.name} completed on ${dateStr}, click to undo`;
            } else {
              ariaLabel = `Mark ${habit.name} complete on ${dateStr}`;
            }

            return (
              <button
                key={dateStr}
                type="button"
                role="gridcell"
                aria-pressed={completed}
                aria-label={ariaLabel}
                title={ariaLabel}
                disabled={isFuture}
                onClick={(e) => void handleDayClick(e, dateStr)}
                style={completed ? { backgroundColor: habitColor } : undefined}
                className={[
                  'relative flex items-center justify-center min-h-9 h-9 rounded text-sm',
                  'transition-colors duration-fast',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                  completed
                    ? 'text-white font-semibold'
                    : 'bg-bg-secondary text-text-primary',
                  isFuture
                    ? 'opacity-30 cursor-not-allowed'
                    : completed
                      ? 'cursor-pointer hover:opacity-90'
                      : 'cursor-pointer hover:bg-hover-bg border border-border',
                  isToday ? 'ring-2 ring-focus-ring ring-offset-0' : '',
                ].join(' ')}
              >
                {completed ? (
                  <span className="flex flex-col items-center justify-center leading-none">
                    <span aria-hidden="true" className="text-[10px]">🔥</span>
                    <span className="text-xs">{day}</span>
                  </span>
                ) : (
                  <span>{day}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

HabitMonthCalendar.displayName = 'HabitMonthCalendar';
